import type { MouseEventHandler, ReactNode } from 'react';

export interface NavbarProps {
  /** Brand area (logo/wordmark) shown at the start. */
  brand: ReactNode;
  /** Left-side icon nav — pass <NavbarLink/> items (rendered inside a <ul>). */
  start?: ReactNode;
  /** Center search slot (e.g. <NavbarSearch/>). */
  search?: ReactNode;
  /** Right-side nav — pass <NavbarLink/> items / dropdowns (rendered inside a <ul>). */
  end?: ReactNode;
  /** Mobile sidebar toggle handler (hamburger, shown < lg). */
  onToggleSidebar?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Mobile secondary-sidebar toggle handler (shown < lg, only when provided). Renders a
   * second toggle button beside the hamburger for layouts with two sidebars.
   */
  onToggleSecondarySidebar?: MouseEventHandler<HTMLButtonElement>;
  /** Stick to the top of the viewport on scroll. Default true. */
  sticky?: boolean;
  className?: string;
}

/** Limitless dark top navbar (header). Faithful to layout_1/full, made sticky by default. */
export function Navbar({
  brand,
  start,
  search,
  end,
  onToggleSidebar,
  onToggleSecondarySidebar,
  sticky = true,
  className = '',
}: NavbarProps) {
  const classes = [
    'navbar navbar-dark navbar-expand-lg navbar-static',
    'border-bottom border-bottom-white border-opacity-10',
    sticky ? 'sticky-top' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} style={sticky ? { zIndex: 1030 } : undefined}>
      <div className="container-fluid">
        <div className="d-flex d-lg-none me-2">
          <button
            type="button"
            className="navbar-toggler sidebar-mobile-main-toggle rounded-pill"
            onClick={onToggleSidebar}
          >
            <i className="ph-list" />
          </button>
          {onToggleSecondarySidebar && (
            <button
              type="button"
              className="navbar-toggler sidebar-mobile-secondary-toggle rounded-pill"
              onClick={onToggleSecondarySidebar}
            >
              <i className="ph-arrow-left" />
            </button>
          )}
        </div>

        <div className="navbar-brand flex-1 flex-lg-0">{brand}</div>

        {start && <ul className="nav flex-row">{start}</ul>}

        {search && (
          <div
            className="navbar-collapse justify-content-center flex-lg-1 order-2 order-lg-1 collapse"
            id="navbar_search"
          >
            <div className="navbar-search flex-fill position-relative mt-2 mt-lg-0 mx-lg-3">
              {search}
            </div>
          </div>
        )}

        {end && <ul className="nav flex-row justify-content-end order-1 order-lg-2">{end}</ul>}
      </div>
    </div>
  );
}

export interface NavbarLinkProps {
  /** Content rendered before the icon/label inside the toggle (e.g. an <Avatar/>). */
  leading?: ReactNode;
  /** Phosphor icon class, e.g. "ph-bell". */
  icon?: string;
  /** Optional text label after the icon. */
  label?: ReactNode;
  /** Badge content (e.g. a count) shown top-right of the icon. */
  badge?: ReactNode;
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  /** Extra classes on the <li>. */
  className?: string;
  /** Children (e.g. a dropdown menu) rendered after the link — for dropdown items. */
  children?: ReactNode;
  /** Mark the <li> as a navbar dropdown and the link as its toggle. */
  dropdown?: boolean;
  /**
   * Control the dropdown from React instead of Bootstrap's data-API (which is unreliable).
   * When true, no data-bs-toggle is emitted; manage open state yourself and toggle the
   * `show` class on the menu passed as children. `expanded` sets aria-expanded.
   */
  controlled?: boolean;
  expanded?: boolean;
}

/** A navbar icon link / dropdown toggle (<li class="nav-item">…). */
export function NavbarLink({
  leading,
  icon,
  label,
  badge,
  href = '#',
  onClick,
  className = '',
  children,
  dropdown = false,
  controlled = false,
  expanded,
}: NavbarLinkProps) {
  const li = ['nav-item ms-lg-2', dropdown ? 'nav-item-dropdown-lg dropdown' : '', className]
    .filter(Boolean)
    .join(' ');
  const linkClass =
    leading || label
      ? 'navbar-nav-link align-items-center rounded-pill p-1'
      : 'navbar-nav-link navbar-nav-link-icon rounded-pill';
  return (
    <li className={li}>
      <a
        href={href}
        className={linkClass}
        {...(dropdown && !controlled ? { 'data-bs-toggle': 'dropdown' } : {})}
        {...(controlled ? { 'aria-expanded': expanded, role: 'button' } : {})}
        onClick={(e) => {
          if (href === '#') e.preventDefault();
          onClick?.(e);
        }}
      >
        {leading}
        {icon && <i className={icon} />}
        {label && <span className="d-none d-lg-inline-block mx-lg-2">{label}</span>}
        {badge != null && (
          <span className="badge bg-yellow text-black position-absolute top-0 end-0 translate-middle-top zindex-1 rounded-pill mt-1 me-1">
            {badge}
          </span>
        )}
      </a>
      {children}
    </li>
  );
}

export interface NavbarSearchProps {
  placeholder?: string;
}

/** Limitless navbar search input with a leading icon. */
export function NavbarSearch({ placeholder = 'Search' }: NavbarSearchProps) {
  return (
    <div className="form-control-feedback form-control-feedback-start flex-grow-1" data-color-theme="dark">
      <input type="text" className="form-control bg-transparent rounded-pill" placeholder={placeholder} />
      <div className="form-control-feedback-icon">
        <i className="ph-magnifying-glass" />
      </div>
    </div>
  );
}
